import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { Box, Center, Stack, Text, UnstyledButton } from '@cowbell/ui/core';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useMooConfig,
  useShellAgents,
  useShellContexts,
  useStarters,
} from '../../provider';
import type { Agent, Starter } from '../../types';
import type {
  Context,
  EntityContext,
  ScreenContext,
} from '../../integrations/context-types';
import { motion } from '../Motion';
import { AgentDescription } from './AgentDescription';
import { greetingFor } from './greeting';
import styles from './EmptyState.module.css';

export interface EmptyStateProps {
  /** Currently-selected agent. May be null while the catalog is still
   *  loading and there is no selection yet. */
  readonly agent: Agent | null;
  /** Called when the user picks a starter — host wires this to the
   *  composer submit path. `pageContext` is set when the starters were
   *  drawn from a page context the user had dismissed: the host attaches
   *  it again and sends with it, so the agent knows what the starter
   *  refers to. */
  readonly onStarterClick: (label: string, pageContext?: Context) => void;
}

const STARTER_ICON_PX = 18;

/** Stagger index, read by `animation-delay` in EmptyState.module.css. */
const staggerStyle = (index: number) =>
  ({ '--moo-stagger': index } as CSSProperties);

// Common keys an entity's display name might live under in `entityData`.
// route-context-source.ts (the console host) writes `name`; other hosts
// may populate a richer entity object under a different key.
const NAME_KEYS = [
  'name',
  'companyName',
  'insuredName',
  'displayName',
  'title',
  'subject',
];

function entityNameFrom(
  data: Record<string, unknown> | undefined
): string | undefined {
  if (!data) return undefined;
  for (const key of NAME_KEYS) {
    const v = data[key];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return undefined;
}

function firstPageContext(
  contexts: readonly Context[]
): EntityContext | ScreenContext | undefined {
  return contexts.find(
    (c): c is EntityContext | ScreenContext =>
      c.kind === 'screen' || c.kind === 'entity'
  );
}

function startersParamsFor(ctx: EntityContext | ScreenContext | undefined): {
  view?: string;
  subTab?: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
} {
  if (ctx?.kind === 'screen') {
    return {
      view: ctx.view,
      subTab: ctx.subTab,
      entityType: ctx.entityType,
      entityId: ctx.entityId,
      entityName: entityNameFrom(ctx.entityData),
    };
  }
  if (ctx?.kind === 'entity') {
    return {
      entityType: ctx.entityType,
      entityId: ctx.entityId,
      entityName: entityNameFrom(ctx.entityData),
    };
  }
  return {};
}

export function EmptyState({ agent, onStarterClick }: EmptyStateProps) {
  const config = useMooConfig();
  const agents = useShellAgents();
  const shellContexts = useShellContexts();

  const isDefault = !!agent && agent.id === agents.defaultAgentId;
  // Fixed at mount so the greeting does not flip while the panel is open.
  const greeting = useMemo(
    () => greetingFor(new Date(), config.userDisplayName),
    [config.userDisplayName]
  );
  const title = isDefault || !agent ? greeting : agent.name;
  const description = agent?.description ?? '';

  // Dismissing the page's chip keeps the page's starters. Picking one hands
  // the dismissed context back so it is attached again for that message.
  const attachedPage = firstPageContext(shellContexts.merged);
  const dismissedPage = attachedPage
    ? undefined
    : firstPageContext(shellContexts.dismissedDerived);
  const startersQuery = useStarters(
    agent?.id ?? null,
    startersParamsFor(attachedPage ?? dismissedPage)
  );
  const starters = startersQuery.data?.starters ?? [];

  return (
    <Box className={styles.root}>
      <Box className={`${styles.content} ${motion.fadeIn}`}>
        {agent && config.renderAgentAvatar
          ? config.renderAgentAvatar(agent, { size: 'lg' })
          : null}
        <Text className={styles.title}>{title}</Text>
        {description ? (
          <AgentDescription key={agent?.id} text={description} />
        ) : null}

        <StartersList
          isLoading={startersQuery.isLoading}
          starters={starters}
          renderIcon={config.renderStarterIcon}
          onStarterClick={(label) => onStarterClick(label, dismissedPage)}
        />
      </Box>
    </Box>
  );
}

interface StartersListProps {
  readonly isLoading: boolean;
  readonly starters: readonly Starter[];
  readonly renderIcon?: (icon: string, opts: { size: number }) => ReactNode;
  readonly onStarterClick: (label: string) => void;
}

function StartersList({
  isLoading,
  starters,
  renderIcon,
  onStarterClick,
}: StartersListProps) {
  if (isLoading) {
    return (
      <Stack
        gap={0}
        className={styles.starterList}
        aria-busy="true"
        aria-label="Loading conversation starters"
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} className={styles.skeletonRow}>
            <Box className={styles.skeletonIcon} />
            <Box className={styles.skeletonText} />
          </Box>
        ))}
      </Stack>
    );
  }
  if (starters.length === 0) return null;
  return (
    <Stack gap={0} className={styles.starterList}>
      {starters.map((s, index) => (
        <UnstyledButton
          key={s.id}
          type="button"
          className={`${styles.starterButton} ${motion.rise}`}
          style={staggerStyle(index)}
          onClick={() => onStarterClick(s.label)}
        >
          <Center className={styles.starterIcon}>
            {renderIcon ? renderIcon(s.icon, { size: STARTER_ICON_PX }) : null}
          </Center>
          <Text className={styles.starterLabel}>{s.label}</Text>
          <FontAwesomeIcon
            icon={faArrowRight}
            className={styles.starterArrow}
            aria-hidden="true"
          />
        </UnstyledButton>
      ))}
    </Stack>
  );
}
